import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react"

type PopoverId = string

interface ConversationsPopoverContextValue {
  filterSection: string | null
  setFilterSection: (section: string | null) => void
  activePopover: PopoverId | null
  getOpenChangeHandler: (id: PopoverId) => (open: boolean) => void
}

const ConversationsPopoverContext =
  createContext<ConversationsPopoverContextValue | null>(null)

export function ConversationsPopoverProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [filterSection, setFilterSection] = useState<string | null>(null)
  const [activePopover, setActivePopover] = useState<PopoverId | null>(null)

  const getOpenChangeHandler = useCallback(
    (id: PopoverId) => (open: boolean) => {
      if (open) {
        setActivePopover(id)
      } else {
        if (id === "filters") {
          setFilterSection(null)
        }
        setActivePopover((current) => (current === id ? null : current))
      }
    },
    [],
  )

  const contextValue = useMemo(
    () => ({
      activePopover,
      getOpenChangeHandler,
      filterSection,
      setFilterSection,
    }),
    [activePopover, getOpenChangeHandler, filterSection],
  )

  return (
    <ConversationsPopoverContext.Provider value={contextValue}>
      {children}
    </ConversationsPopoverContext.Provider>
  )
}

export function useConversationsPopover(id: PopoverId) {
  const ctx = useContext(ConversationsPopoverContext)
  if (!ctx) {
    throw new Error(
      "useConversationsPopover must be used within ConversationsPopoverProvider",
    )
  }

  const isOpen = ctx.activePopover === id
  const onOpenChange = ctx.getOpenChangeHandler(id)

  return {
    isOpen,
    onOpenChange,
    filterSection: ctx.filterSection,
    setFilterSection: ctx.setFilterSection,
  }
}
